'use client';

import { ColumnDef, RowSelectionState, VisibilityState, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trackSearchResultClick } from '@/lib/analytics';
import { generateCompanySlug } from '@/lib/company-utils';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { DataGrid, DataGridContainer } from '@/components/ui/data-grid';
import { DataGridPagination } from '@/components/ui/data-grid-pagination';
import { DataGridTable } from '@/components/ui/data-grid-table';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { CompanySearchResult } from '@/lib/actions/company-search';
import { TOGGLEABLE_COLUMNS, columnLabelKey } from './resultsTableColumns';

export function SortIcon({ field, sort }: { field: string; sort?: string }) {
  const isActive = sort?.startsWith(field);
  const isAsc = sort === `${field}-asc`;
  return (
    <ChevronDown
      className={cn(
        'w-3 h-3 transition-all',
        isActive ? 'opacity-100' : 'opacity-20',
        isAsc && 'rotate-180',
      )}
    />
  );
}

export function MobilePagination({
  offset,
  limit,
  total,
  totalLabel,
  onOffsetChange,
}: {
  offset: number;
  limit: number;
  total: number;
  totalLabel: string;
  onOffsetChange: (offset: number) => void;
}) {
  const { t } = useTranslation();
  const cappedTotal = Math.min(total, 5000);
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(cappedTotal / limit));
  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;

  return (
    <div className="flex items-center justify-between gap-3 mt-2 pb-4">
      <Button
        variant="outline"
        size="sm"
        disabled={isFirst}
        onClick={() => onOffsetChange(Math.max(0, offset - limit))}
      >
        {t('common.buttons.previous', 'Zurück')}
      </Button>
      <span className="text-xs text-muted-foreground text-center">
        <span className="block">
          {t('search.showingRange', {
            from: offset + 1,
            to: Math.min(offset + limit, total, 5000),
            total: totalLabel,
          })}
        </span>
        {t('common.labels.pagination', {
          current: currentPage,
          total: totalPages,
          defaultValue: 'Seite {{current}} von {{total}}',
        })}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={isLast}
        onClick={() => onOffsetChange(offset + limit)}
      >
        {t('common.buttons.next', 'Weiter')}
      </Button>
    </div>
  );
}

// Logged-in users' column picker. Desktop only — the mobile view renders
// cards, not a column-based table, so the picker has nothing to act on there.
export function ColumnsDropdown({
  t,
  country,
  columnVisibility,
  onChange,
  locked = false,
  onLockedClick,
}: {
  t: ReturnType<typeof useTranslation>['t'];
  country?: string;
  columnVisibility: VisibilityState;
  onChange: (id: string, visible: boolean) => void;
  // When locked (non-Pro), the trigger doesn't open the menu — it calls
  // onLockedClick (which surfaces the "Pro feature" prompt) instead.
  locked?: boolean;
  onLockedClick?: () => void;
}) {
  const trigger = (
    <Button
      variant="outline"
      size="sm"
      className="hidden lg:inline-flex h-auto min-h-7 py-1 text-[11px] gap-1 rounded-full"
      onClick={locked ? onLockedClick : undefined}
    >
      <SlidersHorizontal className="h-3 w-3 shrink-0" />
      {t('search.columns', 'Spalten')}
    </Button>
  );

  if (locked) return trigger;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[60vh] overflow-y-auto"
      >
        <DropdownMenuLabel>
          {t('search.columnsLabel', 'Sichtbare Spalten')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {TOGGLEABLE_COLUMNS.map((col) => (
          <DropdownMenuCheckboxItem
            key={col.id}
            checked={!!columnVisibility[col.id]}
            // Keep the menu open so several columns can be toggled at once.
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) => onChange(col.id, !!checked)}
          >
            {t(
              `search.table.${columnLabelKey(col.id, col.labelKey, country)}`,
            )}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TableImplementation({
  previewEuId,
  onOpenPreview,
  data,
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  total,
  totalLabel,
  locale,
  offset,
  limit,
  onOffsetChange,
  onLimitChange,
  rowSelection,
  onRowSelectionChange,
}: {
  previewEuId?: string | null;
  onOpenPreview?: (euId: string, trigger?: HTMLElement) => void;
  data: CompanySearchResult[];
  columns: ColumnDef<CompanySearchResult>[];
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (id: string, visible: boolean) => void;
  total: number;
  totalLabel: string;
  locale: import('@/lib/i18n-routes').Locale;
  offset: number;
  limit: number;
  onOffsetChange?: (offset: number) => void;
  onLimitChange?: (limit: number) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: (next: RowSelectionState) => void;
}) {
  const { t } = useTranslation();
  // Offset-based pagination expressed as TanStack pagination state so the
  // shared DataGridPagination (page numbers + rows-per-page) can drive it. The
  // total is capped at 5000 for paging, matching the sidebar/mobile pagers.
  const pageSize = limit || 50;
  const pagination = {
    pageIndex: Math.floor(offset / pageSize),
    pageSize,
  };
  const pageCount = Math.max(1, Math.ceil(Math.min(total, 5000) / pageSize));
  const table = useReactTable({
    data,
    columns,
    state: { columnVisibility, pagination, rowSelection },
    manualPagination: true,
    pageCount,
    // Keyed by eu_id, not row index: a selection has to survive paging and
    // re-sorting, where index means a different company.
    getRowId: (row) => row.euId,
    enableRowSelection: true,
    onRowSelectionChange: (updater) => {
      onRowSelectionChange(
        typeof updater === 'function' ? updater(rowSelection) : updater,
      );
    },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(pagination) : updater;
      if (next.pageSize !== pagination.pageSize) {
        // Changing page size resets to the first page (handleLimitChange does
        // offset:0 on the parent).
        onLimitChange?.(next.pageSize);
      } else if (next.pageIndex !== pagination.pageIndex) {
        onOffsetChange?.(next.pageIndex * pagination.pageSize);
      }
    },
    // Visibility is owned by the parent (persisted to user settings); funnel
    // any table-originated change back through the same handler.
    onColumnVisibilityChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(columnVisibility) : updater;
      const prevKeys = new Set(Object.keys(columnVisibility));
      for (const [id, visible] of Object.entries(next)) {
        if (!prevKeys.has(id) || columnVisibility[id] !== visible) {
          onColumnVisibilityChange(id, visible);
        }
      }
    },
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 50,
      maxSize: 500,
    },
  });

  return (
    <DataGridContainer className="h-full flex flex-col border-none rounded-none overflow-hidden min-w-0 relative">
      <DataGrid<CompanySearchResult>
        table={table}
        recordCount={total}
        onRowClick={(row, event) => {
          if (
            event.button !== 0 || event.metaKey || event.ctrlKey ||
            event.shiftKey || event.altKey
          ) return;
          const position = data.findIndex((r) => r.euId === row.euId);
          trackSearchResultClick(row.euId, offset + position);
          onOpenPreview?.(row.euId, event.currentTarget);
        }}
        onRowAuxClick={(row, event) => {
          if (event.button !== 1) return;
          event.preventDefault();
          const position = data.findIndex((r) => r.euId === row.euId);
          trackSearchResultClick(row.euId, offset + position);
          const slug = generateCompanySlug(
            {
              name: row.name,
              sitz: row.city,
              registerCourt: row.registerCourt,
              registerType: row.registerType,
              registerId: row.registerId,
            },
            locale,
          );
          window.open(slug, '_blank', 'noopener');
        }}
        rowClassName={(row) =>
          row.euId === previewEuId ? 'bg-primary/5' : undefined
        }
        tableLayout={{
          headerSticky: true,
          headerBorder: true,
          rowBorder: true,
          stripped: false,
          dense: true,
          width: 'fixed',
          columnsResizable: true,
          cellBorder: false,
        }}
        tableClassNames={{
          base: 'min-w-full lg:w-max',
          header:
            'bg-gray-50/80 dark:bg-zinc-900/90 backdrop-blur-sm text-[10px] font-bold text-gray-500 dark:text-zinc-300 uppercase tracking-widest',
          headerRow: 'h-10',
          body: 'text-gray-700 dark:text-zinc-300 font-sans',
          bodyRow:
            'h-9 hover:bg-blue-50/20 dark:hover:bg-white/5 border-b dark:border-white/10 transition-colors',
        }}
      >
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden lg:overflow-auto pb-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px)+0.75rem)] lg:pb-0 scroll-pb-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px)+0.75rem)] lg:scroll-pb-0 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-zinc-700">
          <DataGridTable />
        </div>
        {/* Bottom pagination bar with rows-per-page dropdown, same as /vergabe. */}
        <div className="shrink-0 border-t px-3 py-2">
          <DataGridPagination
            sizes={[25, 50, 100]}
            totalLimit={5000}
            info={t('search.showingRange', {
              from: data.length ? offset + 1 : 0,
              to: Math.min(offset + data.length, total, 5000),
              total: totalLabel,
            })}
          />
        </div>
      </DataGrid>
    </DataGridContainer>
  );
}
